import { Component, computed, effect, inject, input, output } from '@angular/core';
import { BranchLicense, BranchWithBusiness, BusinessBranch, getFullAddress } from 'src/model/business';
import { BusinessStore } from 'src/model/business/business-store';
import { Company } from 'src/model/company.model';
import { BusinessBranchCompanyListComponent } from '../BusinessLicense/business-branch-company-list/business-branch-company-list.component';
import { MatTableController } from '../mat-table-controller';
import { SharedService } from '../Shared/shared-service.service';

@Component({
  selector: 'app-business-branch-list',
  standalone: false,
  templateUrl: './business-branch-list.component.html',
  styleUrl: './business-branch-list.component.scss',
  providers: [MatTableController]
})
export class BusinessBranchListComponent {
  private sharedService = inject(SharedService);
  private store = inject(BusinessStore);
  protected table = inject(MatTableController<BranchWithBusiness>);

  businessList = this.store.businessList;
  businessMap = this.store.businessNameMap;
  branchList = this.store.branchList;
  licenseList = this.store.licenseList;
  activeLicenseList = this.store.activeLicenseList;
  activeLicenseByBranch = this.store.activeLicenseByBranch;

  companyList = input.required<Company[]>();
  searchText = input<string>();
  companyBranchList = computed(() => this.sharedService.groupByNumKey(this.companyList() ?? [], 'BranchId'));
  branchLicenseList = computed<BranchLicense[]>(() => {
    const branches = this.branchList();
    const map = this.businessMap();
    return branches.map(b => ({
      ...b,
      BusinessName: map.get(b.BusinessId),
      LinkedCompanyCount: this.companyBranchList()[b.Id]?.length || 0,
      ...(this.activeLicenseByBranch().size > 0 && { ActiveLicenseCount: this.activeLicenseByBranch().get(b.Id)?.length || 0 })
    })).sort((a, b) => a.BusinessName.toLowerCase().localeCompare(b.BusinessName));
  });

  branchIdFilter = output<number>();
  showLicenseOfBusinessBranch = output<{name: string, branchId: number}>();

  isBoss = computed(() => this.sharedService.isBossAccount());
	displayedColumns = computed(() => this.isBoss() ? [...this.table.columnDefs(), 'Actions'] : this.table.columnDefs());

  constructor() {
    this.table.config.set([
      { columnDef: 'BusinessName', header: 'Business Name' },
      { columnDef: 'Name', header: 'Branch Alias' },
      { columnDef: 'ContactNo', header: 'Contact No.' },
      { columnDef: 'Email', header: 'Email' },
      { columnDef: 'Address', header: 'Address', calculated: (row) => getFullAddress(row.Address) },
      { columnDef: 'LinkedCompanyCount', header: 'Linked Company', onClick: (row) => this.showBranchCompanyList(row) },
      { columnDef: 'ActiveLicenseCount', header: 'Active License', onClick: row => this.goToLicense(row.BusinessName, row.Id) },
    ]);
    effect(() => {
      this.refreshDataSource();
    })
  }

  refreshDataSource() {
    this.table.rawData.set([{ items: this.branchLicenseList() }]);
    this.table.filterQuery.set(this.searchText() ?? '');
  }

  updateBranch(branch: BusinessBranch) {
    this.store.updateBusinessBranch(branch);
  }

  addLicense(branch: BusinessBranch) {
    this.store.addLicense(branch);
  }

  showBranchCompanyList(branch: BranchWithBusiness) {
    const modalRef = this.sharedService.openModal(BusinessBranchCompanyListComponent);
    modalRef.componentInstance.companyList = this.companyBranchList()[branch.Id];
    modalRef.componentInstance.branch = branch;
  }

  goToLicense(name: string, branchId: number) {
    this.showLicenseOfBusinessBranch.emit({ name, branchId });
  }
}
